/**
 * Голосование — ТОЧНАЯ КОПИЯ legacy htdocs/core/templates/main_votes.tpl
 * (ветка `isset($data.voteinfo)`, т.е. votes() при id >= 1) вместе с inline-JS:
 *
 *  - confirmBox (bootstrap-модалка) на кнопку «Проголосовать»:
 *    data-confirm="Вы уверены? Изменить результат голосования в дальнейшем будет нельзя!"
 *  - ограничение для question_type == 'candidate': больше трёх «За» выбрать нельзя,
 *    показывается модалка «Выбрать можно только 3х кандидатов», выбор откатывается.
 *
 * Ответы: 1=За, 2=Против, 3=Воздержался («Воздержался» отмечен по умолчанию — как legacy).
 * Переголосовать нельзя: если по вопросу уже есть ответ, радиокнопки не выводятся,
 * вместо них — выбранный вариант в кавычках.
 *
 * Bootstrap-JS в реворке нет, поэтому модалка рендерится сразу в показанном виде
 * (.modal.fade.in + display:block + .modal-backdrop) — ровно то, что делал bootstrap.
 */

import { useEffect, useState, type CSSProperties, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'

import { api } from '@/api/client'
import type { Vote, VoteQuestion } from '@/api/types'
import { useApi } from '@/lib/useApi'

/** Строка результатов: SELECT question, CASE ... AS case, count(answer) AS names. */
interface VoteResult {
  question: string
  case: string
  names: string | number
}

/** GET /api/votes/{id} (Handlers\User\Votes::show). */
interface VoteData {
  vote: Vote
  questions: VoteQuestion[]
  /** vote_qid → 1|2|3 (legacy $data.votes_answer) */
  my_answers: Record<number, number>
  /** вопрос → вариант → строка результата (legacy $data.votes_results) */
  results: Record<string, Record<string, VoteResult>>
  question_type: string
  date_end: string
}

const ANSWER_LABEL: Record<number, string> = { 1: 'За', 2: 'Против', 3: 'Воздержался' }

/** legacy: style="float:center" — невалидный CSS, но переносим 1:1. */
const FLOAT_CENTER = { float: 'center' } as unknown as CSSProperties

const CONFIRM_TEXT = 'Вы уверены? Изменить результат голосования в дальнейшем будет нельзя!'

/** legacy сравнивает строки: $data.date_end < date('Y-m-d'). */
function today(): string {
  const now = new Date()
  const pad = (n: number): string => (n < 10 ? '0' + String(n) : String(n))
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export function VotePage() {
  const { id } = useParams<{ id: string }>()
  const { data, reload } = useApi<VoteData>(id !== undefined ? `/api/votes/${id}` : null)

  /** vote_qid → выбранный вариант. legacy: «Воздержался» (3) отмечен по умолчанию. */
  const [answers, setAnswers] = useState<Record<number, number>>({})
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [candidateLimit, setCandidateLimit] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (data === null) return
    const initial: Record<number, number> = {}
    for (const question of data.questions) {
      if (data.my_answers[question.id] === undefined) initial[question.id] = 3
    }
    setAnswers(initial)
  }, [data])

  if (data === null) return <div />

  const now = today()
  const voted = Object.keys(data.my_answers).length > 0 // legacy: isset($data.votes_answer)
  const finished = data.date_end < now
  const open = data.date_end > now
  const resultQuestions = Object.keys(data.results)

  /** legacy: $('.radioza').on('click') — при question_type == 'candidate' не более 3х «За». */
  function choose(questionId: number, value: number): void {
    const next = { ...answers, [questionId]: value }
    if (data !== null && data.question_type === 'candidate' && value === 1) {
      const count = Object.values(next).filter((answer) => answer === 1).length
      if (count > 3) {
        setCandidateLimit(true)
        return
      }
    }
    setAnswers(next)
  }

  /** legacy: dialog() на input[data-confirm] — сначала модалка, потом submit формы. */
  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    setConfirmOpen(true)
  }

  /** legacy: yesBtn → форма уходит POST'ом с answer[qid] по всем незакрытым вопросам. */
  async function submitVote(): Promise<void> {
    setConfirmOpen(false)
    setBusy(true)
    try {
      await api.post(`/api/votes/${String(id)}`, { answers })
      reload()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <br />
      <div
        style={{
          fontSize: '22px',
          fontWeight: 600,
          paddingBottom: '5px',
          textAlign: 'center',
        }}
      >
        {data.vote.name}
      </div>

      {resultQuestions.length > 0 && finished && (
        <>
          <div style={{ fontSize: '20px', paddingBottom: '10px' }}>
            Голосование завершено со следующими результатами:
          </div>
          <div>
            <table className="table table-hover tdbottomborder">
              <tbody>
                {resultQuestions.map((question) => (
                  <tr key={question}>
                    <td>{question}</td>
                    <td>
                      <div className="descnowrap" style={{ display: 'flex' }}>
                        {Object.values(data.results[question]).map((result) => (
                          <div className="mobvotesd" key={result.case}>
                            &quot;{result.case}&quot;{'  -  '}
                            {result.names}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {voted && (
        <>
          <br />
          <div style={{ fontSize: '20px', paddingBottom: '10px' }}>
            Вы завершили данное голосование, ваш выбор:
          </div>
        </>
      )}

      {!voted && finished ? (
        <>
          <br />
          <div style={{ fontSize: '20px', paddingBottom: '10px' }}>
            Вы не учавствовали в данном голосовании
          </div>
        </>
      ) : (
        <>
          <form method="post" onSubmit={onSubmit}>
            <table className="table table-hover tdbottomborder">
              <tbody>
                {data.questions.map((question) => {
                  const myAnswer = data.my_answers[question.id]
                  return (
                    <tr key={question.id}>
                      <td>{question.question}</td>
                      <td>
                        {myAnswer !== undefined && <>&quot;{ANSWER_LABEL[myAnswer]}&quot;</>}
                        <div style={{ display: 'flex', paddingLeft: '10px' }}>
                          {myAnswer === undefined && open && (
                            <>
                              <label className="radio-inline">
                                <input
                                  className="radioza"
                                  type="radio"
                                  name={`answer[${question.id}]`}
                                  value="1"
                                  checked={answers[question.id] === 1}
                                  onChange={() => choose(question.id, 1)}
                                />
                                За
                              </label>
                              <label className="radio-inline">
                                <input
                                  className="radioprotiv"
                                  type="radio"
                                  name={`answer[${question.id}]`}
                                  value="2"
                                  checked={answers[question.id] === 2}
                                  onChange={() => choose(question.id, 2)}
                                />
                                Против
                              </label>
                              <label className="radio-inline">
                                <input
                                  className="radiovozder"
                                  type="radio"
                                  name={`answer[${question.id}]`}
                                  value="3"
                                  checked={answers[question.id] === 3}
                                  onChange={() => choose(question.id, 3)}
                                />
                                Воздержался
                              </label>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!voted && open && (
              <div style={FLOAT_CENTER}>
                <input
                  type="submit"
                  name="vote-submit"
                  className="bt"
                  value="Проголосовать"
                  data-confirm={CONFIRM_TEXT}
                  disabled={busy}
                />
              </div>
            )}
          </form>
          {voted && (
            <div style={{ fontSize: '20px' }}>Спасибо, что приняли участие в голосовании!</div>
          )}
        </>
      )}

      {confirmOpen && (
        <>
          <div className="modal fade confirm-modal in" style={{ display: 'block' }}>
            <div className="modal-dialog modal-sm" role="document">
              <div className="modal-content">
                <div className="modal-body pb-5">{CONFIRM_TEXT}</div>
                <div className="modal-footer pt-3 pb-3">
                  <a
                    href="#"
                    className="btn btn-primary yesBtn btn-sm"
                    onClick={(event) => {
                      event.preventDefault()
                      void submitVote()
                    }}
                  >
                    Подтвердить
                  </a>
                  <button
                    type="button"
                    className="btn btn-secondary abortBtn btn-sm"
                    data-dismiss="modal"
                    onClick={() => setConfirmOpen(false)}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade in" />
        </>
      )}

      {candidateLimit && (
        <>
          <div className="modal fade confirm-modal in" style={{ display: 'block' }}>
            <div className="modal-dialog modal-sm" role="document">
              <div className="modal-content">
                <div className="modal-body pb-5">Выбрать можно только 3х кандидатов</div>
                <div className="modal-footer pt-3 pb-3">
                  <button
                    type="button"
                    className="btn btn-secondary abortBtn btn-sm"
                    data-dismiss="modal"
                    onClick={() => setCandidateLimit(false)}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade in" />
        </>
      )}
    </div>
  )
}
